import React, { useState, useEffect, useRef, useImperativeHandle, createElement } from 'react';
import classnames from 'classnames';
import { checkOddEvenIndex, getType, default_icon, useCachedIcons } from '../../utils';
import { ArrowDown } from '../../assets';
import './index.less';
export const TableCeil = (props) => {
    const { record, _index, level, isChild, indexWidth, index, showIcon, highlight, icons, dataIndex, expandOpen, columns, onRowClick, rowStyle, } = props;
    const $index = dataIndex ? record[dataIndex] : _index;
    const [open, setOpen] = useState(expandOpen);
    const renderIndex = (_i) => {
        if (!index) {
            return null;
        }
        const icon = icons?.[_i];
        const highlighted = highlight >= _i + 1;
        return (createElement("div", { className: "ceil", style: { width: indexWidth } }, isChild ? null : icon && showIcon && highlighted ? (createElement("span", null,
            createElement("img", { src: icon, width: "24px", height: "24px", alt: `rank-${_i + 1}` }))) : (createElement("span", { className: classnames('index', {
                highlighted: !!highlighted,
            }) }, _i + 1))));
    };
    const renderExpand = (col) => {
        const Node = (createElement(ArrowDown, { className: "arrow-down-icon", style: { transform: open ? 'rotate(180deg)' : undefined } }));
        return col?.expandExtra ? (col.expandExtra?.(Node, open)) : (createElement(React.Fragment, null,
            open ? `收起` : `展开`,
            Node));
    };
    return (createElement(React.Fragment, null,
        createElement("div", { className: "dv-scroll-row-item", onClick: () => onRowClick(record, _index, level), style: {
                ...rowStyle(record, _index, level),
            } },
            renderIndex($index),
            columns?.map((col, _i) => {
                const value = isChild
                    ? record?.[col?.expandDataIndex || col?.dataIndex]
                    : record?.[col?.dataIndex];
                return (createElement("div", { key: _i, className: classnames('ceil', {
                        [`align-${col.align}`]: !!col.align,
                    }) },
                    col?.render ? col.render?.(value, record, _index, level) : value,
                    record?.children?.length && col?.expandDataIndex ? (createElement("span", { className: "arrow-down", onClick: (e) => {
                            e.stopPropagation?.();
                            setOpen(!open);
                        } }, renderExpand(col))) : null));
            })),
        record?.children?.length && open
            ? record.children.map((child) => (createElement(TableCeil, { ...props, record: child, level: level + 1, isChild: true })))
            : null));
};
const ScrollTable2 = React.forwardRef((props, ref) => {
    const boxRef = useRef(null);
    const contentRef = useRef(null);
    const timerRef = useRef(null);
    const timeoutRef = useRef(null);
    const [anchorIndex, setAnchorIndex] = useState(null);
    const { index, dataIndex, scrollConfig, indexHeader, columns, showHeader, headerBGC, headerColor, dataSource, expandOpen = true, size, indexWidth, showIcon, oddRowBGC, evenRowBGC, rowColor, style, icons = default_icon, highlight, onRowClick, rowSelectStyle, rowStyle, } = props;
    const level = 1;
    const cachedIcons = useCachedIcons(icons);
    const { autoplay = true, step = 1, speed = 100, autoplaySpeed = 2000 } = scrollConfig || {};
    const defaultIndexWidth = typeof indexWidth === 'number' ? `${indexWidth}px` : indexWidth || '50px';
    const gridTemplateColumns = React.useMemo(() => {
        const columnWidths = columns
            .map((item) => (typeof item.width === 'number' ? `${item.width}px` : item.width || '1fr'))
            .join(' ');
        return index ? `${defaultIndexWidth} ${columnWidths}` : columnWidths;
    }, [columns, index, defaultIndexWidth]);
    const handleClearInterval = React.useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);
    const handleClearTimeout = React.useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);
    const handleAutoScroll = React.useCallback(() => {
        if (timerRef.current) {
            handleClearInterval();
        }
        if (!autoplay || !dataSource?.length) {
            return;
        }
        const box = boxRef.current;
        const content = contentRef.current;
        if (!dataSource?.length || box?.offsetHeight >= content?.offsetHeight)
            return;
        timerRef.current = window.setInterval(() => {
            if (content.offsetHeight - (+box.scrollTop + +box.offsetHeight) >= step) {
                box.scrollTop += step;
            }
            else {
                handleClearInterval();
                timeoutRef.current = window.setTimeout(() => {
                    handleClearTimeout();
                    box.scrollTop = 0;
                    timeoutRef.current = window.setTimeout(() => {
                        handleClearTimeout();
                        handleAutoScroll();
                    }, autoplaySpeed);
                }, autoplaySpeed);
            }
        }, speed);
    }, [autoplay, dataSource, speed, step, autoplaySpeed, handleClearInterval, handleClearTimeout]);
    useEffect(() => {
        handleAutoScroll();
    }, [dataSource]);
    useEffect(() => {
        return () => {
            handleClearInterval();
            handleClearTimeout();
        };
    }, []);
    const setSelectedRow = (_index) => {
        setAnchorIndex(_index === anchorIndex ? null : _index);
    };
    const handleRowClick = (record, _index, _level) => {
        onRowClick?.(record, _index, _level);
        setSelectedRow(_index);
    };
    useImperativeHandle(ref, () => ({ setSelectedRow }));
    const renderHeader = () => {
        if (!showHeader) {
            return null;
        }
        const headStyle = {
            backgroundColor: headerBGC,
            color: headerColor,
        };
        return (createElement("div", { className: "header", style: {
                gridTemplateColumns,
                ...headStyle,
            } },
            index ? createElement("div", { className: "header-item" }, indexHeader || '#') : null,
            columns?.map((col, i) => {
                return (createElement("div", { key: col?.key || col?.dataIndex || i, className: classnames('header-item', {
                        [`align-${col.align}`]: !!col.align,
                    }) }, col.title));
            })));
    };
    const renderRowStyle = (record, _index, _level) => {
        const rowStyleDefault = getType(rowStyle) === 'function' ? rowStyle(record, _index, _level) : rowStyle;
        let selectStyle = {};
        if (anchorIndex === _index) {
            selectStyle =
                getType(rowSelectStyle) === 'function'
                    ? rowSelectStyle(record, _index, _level)
                    : rowSelectStyle;
        }
        return {
            gridTemplateColumns,
            background: checkOddEvenIndex(_index + 1) ? evenRowBGC : oddRowBGC,
            color: rowColor,
            ...rowStyleDefault,
            ...selectStyle,
        };
    };
    const renderContent = () => {
        return (createElement("div", { className: "dv-scroll-content", ref: contentRef }, dataSource?.map((record, _index) => {
            return (createElement(React.Fragment, { key: `row-${_index.toString()}` },
                createElement(TableCeil, { record: record, _index: _index, level: level, index: index, indexWidth: defaultIndexWidth, showIcon: showIcon, highlight: highlight, icons: cachedIcons, dataIndex: dataIndex, expandOpen: expandOpen, columns: columns, onRowClick: handleRowClick, rowStyle: renderRowStyle })));
        })));
    };
    return (createElement("div", { className: classnames('dv-scroll-table2', {
            [size]: !!size,
        }), style: style },
        renderHeader(),
        createElement("div", { className: "dv-scroll-rows", ref: boxRef, onMouseEnter: handleClearInterval, onMouseLeave: handleAutoScroll }, renderContent())));
});
export default class DataVScrollTable2 extends React.Component {
    ref;
    constructor(props) {
        super(props);
        this.ref = React.createRef();
    }
    render() {
        return createElement(ScrollTable2, { ...this.props, ref: this.ref });
    }
}
